import React from 'react';
import isEmpty from 'lodash/isEmpty';
import styled, { css } from 'styled-components';
import { removePricesAndCharacteristics } from '@sparknz/journeys-common';
import {
  Box,
  ProductCardFeature,
  ProductCardPrice,
  ProductCardPromotion,
  ProductCardSection,
  Text,
} from '@sparknz/spark-pattern-library';

import { useProductDetails } from 'journey/productDetails/provider';
import { PlanBundleOptions } from 'journey/productDetails/typings';
import { useSegment } from 'shared/hooks';
import { Entitlements, Price, Promotions } from '../../typings';
import {
  getPrice,
  getPromotionDeal,
  getSortedEntitlements,
} from '../../utils/planCard.utils';
import { INFORMATION_ICON_TEXT } from '../constants/planCard.constant';

const Section = styled(ProductCardSection)`
  ${({ theme: { spacing, media } }) => css`
    position: relative;
    display: flex;
    flex-direction: column;
    flex-grow: 0;
    padding-bottom: ${spacing.s2};

    ${media.up('slim')} {
      flex-grow: 1;
    }
  `}
`;

const Badge = styled(Text)`
  ${({ theme: { palette, spacing, borderRadius } }) => css`
    background-color: ${palette.baseColors.greyLynn};
    color: ${palette.baseColors.wairua};
    padding: ${spacing.s1} ${spacing.s2};
    border-radius: ${borderRadius.r1};
    position: absolute;
    top: -16px;
    left: 32px;
  `}
`;

const LineBadge = styled(Badge)`
  left: 128px;
`;

const PlanPrice = styled(ProductCardPrice)`
  ${({ theme: { spacing } }) => css`
    margin-bottom: ${spacing.s5};
  `}
`;

const List = styled.ul`
  ${({ theme: { spacing } }) => css`
    padding: 0;
    margin: 0 0 ${spacing.s3} 0;
  `}
`;

const ListItem = styled.li`
  ${({ theme: { spacing } }) => css`
    display: flex;
    padding: 0;
    margin-bottom: ${spacing.s2};
    list-style: none;
    & svg {
      min-width: 24px;
    }

    &:last-child {
      margin: 0;
    }
  `}
`;

interface PromotionContainerProps {
  variant: 'banner' | 'text';
}

const PromotionContainer = styled(Box)<PromotionContainerProps>`
  ${({ theme: { spacing }, variant }) => css`
    margin-bottom: ${variant === 'banner' ? spacing.s2 : spacing.s1};
  `}
`;

interface BadgeTextProps {
  label: string;
  selectedLineNumber: string;
  shouldDisplayLine: boolean;
}

const BadgeText = ({
  label,
  selectedLineNumber,
  shouldDisplayLine,
}: BadgeTextProps) => {
  if (!label) {
    return null;
  }
  if (shouldDisplayLine) {
    return (
      <>
        <Badge variant="footnoteDemi">{label}</Badge>
        <LineBadge variant="footnoteDemi">{selectedLineNumber}</LineBadge>
      </>
    );
  }
  return <Badge variant="footnoteDemi">{label}</Badge>;
};

interface EntitlementListProps {
  entitlements: Entitlements;
  handleInfoIconClick?: (
    contentKey?: string,
    customLink?: string,
  ) => () => void;
}
interface PlanCardTopSectionProps extends EntitlementListProps {
  badgeLabel?: string;
  description?: string;
  name: string;
  price: Price;
  promotions?: Promotions;
}

const EntitlementList = ({
  entitlements,
  handleInfoIconClick,
}: EntitlementListProps) => {
  const sortedEntitlements = getSortedEntitlements(entitlements);
  return (
    <List>
      {sortedEntitlements.map(
        ({ contentKey, name, iconUrl, longDescription }) => {
          const customLink = `${name} ${INFORMATION_ICON_TEXT}`;

          return (
            <ListItem key={name}>
              <ProductCardFeature
                iconUrl={iconUrl ?? null}
                title={name}
                description={longDescription || ''}
                handleInfoIconClick={handleInfoIconClick(
                  contentKey,
                  customLink,
                )}
              />
            </ListItem>
          );
        },
      )}
    </List>
  );
};

const PlanCardTopSection = ({
  badgeLabel,
  description,
  entitlements,
  handleInfoIconClick,
  name,
  price,
  promotions,
}: PlanCardTopSectionProps) => {
  const { isBusiness } = useSegment();
  const {
    ifpEligibility: { selectedLineNumber },
  } = useProductDetails();

  const promotionVariant = isBusiness ? 'text' : 'banner';

  const pricePeriod =
    price?.frequency?.value > 1
      ? `${price.frequency.value} ${price.frequency.period.toLowerCase()}s`
      : price.frequency.period?.toLowerCase();

  const promotionDeal = getPromotionDeal(promotions);

  const shouldDisplayLine =
    selectedLineNumber &&
    selectedLineNumber !== PlanBundleOptions.ChooseNewPlan;

  return (
    <Section>
      <BadgeText
        label={badgeLabel}
        selectedLineNumber={selectedLineNumber}
        shouldDisplayLine={shouldDisplayLine}
      />
      <Text variant="subheadingM" mb="s1">
        {removePricesAndCharacteristics(name)}
      </Text>
      <PlanPrice
        price={{
          priceValue: getPrice(price, isBusiness),
          pricePeriod: pricePeriod || '',
          isGstExcl: isBusiness,
        }}
      />

      {promotionDeal?.name ? (
        <PromotionContainer variant={promotionVariant}>
          <ProductCardPromotion
            variant={promotionVariant}
            title={promotionDeal.name}
            description={promotionDeal.longDescription}
          />
        </PromotionContainer>
      ) : null}

      {description ? (
        <Text variant="bodySDemi" mb="s4">
          {description}
        </Text>
      ) : null}

      {!isEmpty(entitlements) ? (
        <EntitlementList
          entitlements={entitlements}
          handleInfoIconClick={handleInfoIconClick}
        />
      ) : null}
    </Section>
  );
};

export default PlanCardTopSection;
